<script setup lang="ts">
// Android-like on-screen keyboard (hodgef/simple-keyboard, local npm dep — no CDN). Bottom-docked,
// dark, rounded, large touch targets. Mounted by PlayerSearchList.vue ONLY while its search/guest
// -name input has real DOM focus — this component never listens globally and never competes with
// the wedge scanner (src/scanner.ts already stands down whenever a text input is focused, so the
// scanner and this keyboard are mutually exclusive by construction, nothing to coordinate here).
//
// Two-way bound to the input's v-model: onChange writes typed keys back to the parent's ref, and
// an external change to that ref (e.g. the parent clearing it) is pushed back into simple-keyboard
// via setInput so the two never drift apart.
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SimpleKeyboard from 'simple-keyboard'
import 'simple-keyboard/build/css/index.css'

const props = defineProps<{ modelValue: string }>()
const emit = defineEmits<{ (e: 'update:modelValue', value: string): void; (e: 'done'): void }>()

const rootEl = ref<HTMLDivElement | null>(null)
let keyboard: SimpleKeyboard | null = null
let layoutName = 'default'

function setLayoutName(name: string) {
  layoutName = name
  keyboard?.setOptions({ layoutName })
}

function onKeyPress(button: string) {
  if (button === '{shift}') {
    setLayoutName(layoutName === 'default' ? 'shift' : 'default')
  } else if (button === '{numbers}' || button === '{abc}') {
    setLayoutName(layoutName === 'numbers' ? 'default' : 'numbers')
  } else if (button === '{enter}') {
    emit('done')
  }
}

function onChange(input: string) {
  emit('update:modelValue', input)
}

onMounted(() => {
  if (!rootEl.value) return
  keyboard = new SimpleKeyboard(rootEl.value, {
    onChange,
    onKeyPress,
    theme: 'hg-theme-default kiosk-keyboard',
    layout: {
      default: ['q w e r t y u i o p', 'a s d f g h j k l', "{shift} z x c v b n m {backspace}", "{numbers} {space} ' {enter}"],
      shift: ['Q W E R T Y U I O P', 'A S D F G H J K L', '{shift} Z X C V B N M {backspace}', "{numbers} {space} ' {enter}"],
      numbers: ['1 2 3 4 5 6 7 8 9 0', '- _ . , @ / : ; ( )', '{abc} {space} {backspace} {enter}'],
    },
    display: {
      '{shift}': '⇧',
      '{backspace}': '⌫',
      '{space}': 'space',
      '{enter}': 'Done',
      '{numbers}': '123',
      '{abc}': 'ABC',
    },
    mergeDisplay: true,
    physicalKeyboardHighlight: false,
    preventMouseDownDefault: true,
  })
  keyboard.setInput(props.modelValue ?? '')
})

// Keep simple-keyboard's internal buffer in sync if the bound value changes from outside (e.g.
// the parent resetting the search query) instead of from this keyboard's own onChange.
watch(
  () => props.modelValue,
  (v) => {
    if (keyboard && keyboard.getInput() !== (v ?? '')) keyboard.setInput(v ?? '')
  },
)

onBeforeUnmount(() => {
  keyboard?.destroy()
  keyboard = null
})
</script>

<template>
  <div
    class="fixed inset-x-0 bottom-0 z-[70] onscreen-keyboard-dock"
    @mousedown.prevent
    @touchstart.prevent
  >
    <div ref="rootEl" class="onscreen-keyboard-target"></div>
  </div>
</template>

<style scoped>
.onscreen-keyboard-dock {
  background: #101012;
  padding: 8px 8px calc(8px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.5);
}

/* Passing simple-keyboard an existing container (rather than a bare selector string) makes it
   reuse THAT element as its root instead of creating an inner `.simple-keyboard` wrapper — so
   `.onscreen-keyboard-target` (this component's own ref target above) is what actually carries
   `hg-theme-default`'s rules (light-grey background, 5px padding), not `.simple-keyboard`.
   !important keeps this theming deterministic regardless of how the two stylesheets end up
   ordered in the build, rather than relying on cascade order. Row/button spacing is left to the
   library's own defaults (5px), which already reads fine. */
.onscreen-keyboard-target {
  max-width: none !important;
  width: 100%;
  background: transparent !important;
  padding: 0 !important;
}

:deep(.hg-button) {
  height: 48px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border: none !important;
  border-radius: 10px !important;
  background: #2c2c2e !important;
  color: #f2f2f2;
  font-size: 16px;
  font-weight: 600;
  box-shadow: none !important;
}

:deep(.hg-button.hg-activeButton) {
  background: #48484a !important;
}

:deep(.hg-button-space) {
  flex-grow: 6 !important;
}

:deep(.hg-button-enter) {
  flex-grow: 2 !important;
  background: #059669 !important;
}
:deep(.hg-button-enter.hg-activeButton) {
  background: #10b981 !important;
}

:deep(.hg-button-shift),
:deep(.hg-button-backspace),
:deep(.hg-button-numbers),
:deep(.hg-button-abc) {
  flex-grow: 1.6 !important;
}
</style>
